import { useEffect, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface SectionNavItem {
  id: string
  label: string
  icon: LucideIcon
  to: string
  hint?: ReactNode
  marker?: ReactNode
}

export interface SectionNavGroup {
  label: string
  items: SectionNavItem[]
}

const PANEL_FROM = {
  lg: { row: 'lg:hidden', panel: 'lg:block' },
  xl: { row: 'xl:hidden', panel: 'xl:block' }
} as const

export function SectionNav({
  label,
  groups,
  active,
  panelFrom = 'lg',
  replace
}: {
  label: string
  groups: SectionNavGroup[]
  active: string
  panelFrom?: keyof typeof PANEL_FROM
  replace?: boolean
}) {
  const layout = PANEL_FROM[panelFrom]
  const rowRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const row = rowRef.current
    const current = row?.querySelector('[aria-current="page"]')
    if (!row || !current) return
    const rowBox = row.getBoundingClientRect()
    const box = current.getBoundingClientRect()
    row.scrollLeft += box.left - rowBox.left - (rowBox.width - box.width) / 2
  }, [active])

  return (
    <>
      <nav ref={rowRef} aria-label={label} className={cn('-mx-4 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6', layout.row)}>
        <ul className="flex w-max gap-1.5">
          {groups
            .flatMap(group => group.items)
            .map(({ id, label: itemLabel, icon: Icon, to, marker }) => {
              const current = id === active
              return (
                <li key={id}>
                  <Button asChild variant={current ? 'secondary' : 'ghost'} size="sm" className={cn('relative', current && 'ring-1 ring-border')}>
                    <Link to={to} replace={replace} aria-current={current ? 'page' : undefined}>
                      <Icon className={current ? 'text-primary' : 'text-muted-foreground'} />
                      {itemLabel}
                      {marker}
                    </Link>
                  </Button>
                </li>
              )
            })}
        </ul>
      </nav>

      <nav aria-label={label} className={cn('sticky top-4 hidden rounded-2xl border border-border bg-card p-2 shadow-xs', layout.panel)}>
        {groups.map(group => (
          <div key={group.label} className="border-b border-border pb-2 last:border-0 last:pb-0 [&+&]:pt-2">
            <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium text-muted-foreground">{group.label}</p>
            <ul className="space-y-0.5">
              {group.items.map(({ id, label: itemLabel, icon: Icon, to, hint }) => {
                const current = id === active
                return (
                  <li key={id}>
                    <Link
                      to={to}
                      replace={replace}
                      aria-current={current ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        current ? 'bg-accent text-accent-foreground' : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <Icon className={cn('size-4 shrink-0', current ? 'text-primary' : 'text-muted-foreground')} />
                      <span className="min-w-0 truncate">{itemLabel}</span>
                      {hint}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  )
}
